<script lang="ts" setup>
import { type Editor, type JSONContent, useEditor } from "@tiptap/vue-3";
import { StarterKit } from "@tiptap/starter-kit";
import { useUI } from "#ui/composables/useUI";
import { computed, ref, toRef } from "vue";
import appConfig from "#build/app.config";
import { input } from "#ui/ui.config";
import { mergeConfig } from "#ui/utils";
import { twJoin, twMerge } from "tailwind-merge";
import type { InputSize, InputVariant } from "#ui/types";
import { useInjectButtonGroup } from "#ui/composables/useButtonGroup";
import { useFormGroup } from "#ui/composables/useFormGroup";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import type { FieldDefinition } from "~/models/Automation";
import {
    faAlignSlash,
    faPenSwirl,
    faRedo,
    faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { common, createLowlight } from "lowlight";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { useFuse, type UseFuseOptions } from "@vueuse/integrations/useFuse";
import { faBracketsCurly } from "@fortawesome/pro-light-svg-icons";
import Fuse from "fuse.js";

type Modes = {
    html: string;
    text: string;
    json: JSONContent;
};

export interface HdInputProps {
    mode: keyof Modes;
    modelValue: Modes[HdInputProps["mode"]] | null | undefined;
    menuMode?: "icons";
    type: string;
    id?: string;
    name?: string;
    textarea?: boolean;
    placeholder?: string;
    blockType: "list" | "styles";
    styleType: "list" | "styles";
    required?: boolean;
    disabled?: boolean;
    autofocus?: boolean;
    maxWidth?: string;
    snug?: boolean;
    autofocusDelay?: number;
    icon?: string;
    loadingIcon?: string;
    leadingIcon?: string;
    inputClass?: string;
    trailingIcon?: string;
    trailing?: boolean;
    leading?: boolean;
    loading?: boolean;
    padded?: boolean;
    size?: InputSize;
    color?: any;
    variant?: InputVariant;
    inputInnerClass?: string;
    class?: any;
    ui?: any;
    proseModifiers?: any;
    proseStyle?: any;
    modelModifiers?: {
        trim?: boolean | undefined;
        lazy?: boolean | undefined;
        number?: boolean | undefined;
    };
    extensions: any[];
    withBubbleMenu?: boolean;
    withFloatingMenu?: boolean;
    withMenu?: boolean;
    unstyled?: boolean;
    starterKit?: Record<any, any>;
    stripOuterTag?: boolean;
    outerTag?: string;
    outerClass?: string | string[];
    outerStyle?: Partial<CSSStyleDeclaration>;
    fields?: FieldDefinition[];
    mentionMatch?: {
        between: [Prefix, Suffix];
        name: string;
        fields: FieldDefinition[];
    }[];
}

type Prefix = string;
type Suffix = string;

const props = withDefaults(defineProps<HdInputProps>(), {
    mode: "text",
    modelValue: "",
    type: "",
    variant: "outline",
    size: "sm",
    required: false,
    blockType: "list",
    styleType: "list",
    disabled: false,
    autofocus: false,
    trailing: false,
    inputInnerClass: undefined,
    leading: false,
    loading: false,
    padded: true,
    maxWidth: "max-w-none",
    mentionMatch: () => [],
    color: "white",
    class: () => "",
    ui: () => ({}),
    modelModifiers: () => ({}),
    withBubbleMenu: false,
    withFloatingMenu: false,
    withMenu: false,
    extensions: () => [] as any[],
    fields: () => [] as FieldDefinition[],
    starterKit: () => ({}),
    id: undefined,
    name: undefined,
    textarea: undefined,
    placeholder: undefined,
    autofocusDelay: undefined,
    icon: undefined,
    loadingIcon: undefined,
    leadingIcon: undefined,
    trailingIcon: undefined,
    stripOuterTag: undefined,
    outerTag: undefined,
    outerClass: undefined,
    outerStyle: undefined,
});

const emit = defineEmits<{
    (e: "update:modelValue", v: HdInputProps["modelValue"]): void;
}>();

const config = mergeConfig(
    appConfig.ui.strategy,
    props.textarea ? appConfig.ui.textarea : appConfig.ui.input,
    input,
);
const { ui }: { ui: any; attrs: any } = useUI(
    props.textarea ? "textarea" : "input",
    toRef(props, "ui"),
    config,
    toRef(props, "class"),
);
const { size: sizeButtonGroup, rounded } = useInjectButtonGroup({ ui, props });
const {
    emitFormInput,
    size: sizeFormGroup,
    color,
} = useFormGroup(props, config);
const sizeGroup = computed(() => sizeButtonGroup.value || sizeFormGroup.value);
const slots = useSlots();

const inputOuterClass = computed(() => {
    const variant =
        ui.value.color?.[color.value as any]?.[props.variant] ||
        ui.value.variant[props.variant];

    return twMerge(
        twJoin(
            "group",
            ui.value.base,
            rounded.value,
            ui.value.size[sizeGroup.value],
            focused.value
                ? variant
                      ?.replaceAll("{color}", color.value)
                      .replaceAll("focus:", "")
                      .replaceAll("focus-within:", "")
                : variant?.replaceAll("{color}", color.value),
            (isLeading.value || slots.leading) &&
                ui.value.leading.padding[sizeGroup.value],
            (isTrailing.value || slots.trailing) &&
                ui.value.trailing.padding[sizeGroup.value],
        ),
    );
});

const inputInnerClass = computed(() => {
    return twMerge(
        twJoin(
            "[&>:first-child]:!mt-0",
            "[&>:last-child]:!mb-0",
            "focus:outline-none",
            ui.value.placeholder,
            props.padded ? ui.value.padding[sizeGroup.value] : "p-0",
        ),
        props.inputInnerClass,
    );
});

const bubbleMenuClass = computed(() => {
    const variant =
        ui.value.color?.[color.value as any]?.[props.variant] ||
        ui.value.variant[props.variant];
    return twMerge(
        twJoin(
            ui.value.base,
            ui.value.form,
            rounded.value,
            ui.value.placeholder,
            ui.value.size[sizeGroup.value],
            "p-0",
            variant?.replaceAll("{color}", color.value),
        ),
        props.inputInnerClass,
    );
});

const isLeading = computed(() => {
    return (
        (props.icon && props.leading) ||
        (props.icon && !props.trailing) ||
        (props.loading && !props.trailing) ||
        props.leadingIcon
    );
});

const isTrailing = computed(() => {
    return (
        (props.icon && props.trailing) ||
        (props.loading && props.trailing) ||
        props.trailingIcon
    );
});

const leadingIconName = computed(() => {
    if (props.loading) {
        return props.loadingIcon;
    }
    return props.leadingIcon || props.icon;
});

const trailingIconName = computed(() => {
    if (props.loading && !isLeading.value) {
        return props.loadingIcon;
    }
    return props.trailingIcon || props.icon;
});

const leadingWrapperIconClass = computed(() => {
    return twJoin(
        ui.value.icon.leading.wrapper,
        ui.value.icon.leading.pointer,
        ui.value.icon.leading.padding[sizeGroup.value],
    );
});

const leadingIconClass = computed(() => {
    return twJoin(
        ui.value.icon.base,
        color.value &&
            appConfig.ui.colors.includes(color.value) &&
            ui.value.icon.color.replaceAll("{color}", color.value),
        ui.value.icon.size[sizeGroup.value],
        props.loading && ui.value.icon.loading,
    );
});

const trailingWrapperIconClass = computed(() => {
    return twJoin(
        ui.value.icon.trailing.wrapper,
        ui.value.icon.trailing.pointer,
        ui.value.icon.trailing.padding[sizeGroup.value],
    );
});

const trailingIconClass = computed(() => {
    return twJoin(
        ui.value.icon.base,
        color.value &&
            appConfig.ui.colors.includes(color.value) &&
            ui.value.icon.color.replaceAll("{color}", color.value),
        ui.value.icon.size[sizeGroup.value],
        props.loading && !isLeading.value && ui.value.icon.loading,
    );
});

const content = ref(props.modelValue);
const focused = ref<boolean>(false);

const lowlight = createLowlight(common);
const editor = useEditor({
    editable: !props.disabled,
    extensions: [
        Link.configure({
            protocols: ["tel", "mailto"],
        }),
        ...(props.placeholder
            ? [
                  Placeholder.configure({
                      placeholder: props.placeholder,
                  }),
              ]
            : []),
        Underline.configure(),
        TextStyle.configure(),
        Color.extend({
            addGlobalAttributes() {
                return [
                    {
                        types: this.options.types,
                        attributes: {
                            color: {
                                default: null,
                                parseHTML: (element) =>
                                    element.style.color?.replace(/['"]+/g, ""),
                                renderHTML: (attributes) => {
                                    if (!attributes.color) {
                                        return {};
                                    }

                                    if (attributes.color.startsWith("{{")) {
                                        return {
                                            style: `color: ${createFieldCollection(toValue(props.fields)).replaceContent(attributes.color)}`,
                                        };
                                    }

                                    return {
                                        style: `color: ${attributes.color}`,
                                    };
                                },
                            },
                        },
                    },
                ];
            },
        }),
        TextAlign.configure({
            defaultAlignment: "left",
            types: ["paragraph", "heading", "bulletList", "orderedList"],
        }),
        ...(props.mode === "text"
            ? []
            : [
                  CodeBlockLowlight.configure({
                      languageClassPrefix: "lang",
                      lowlight,
                  }),
              ]),
        StarterKit.configure(
            props.mode === "text"
                ? {
                      blockquote: false,
                      bold: false,
                      bulletList: false,
                      code: false,
                      codeBlock: false,
                      dropcursor: false,
                      gapcursor: false,
                      hardBreak: false,
                      heading: false,
                      horizontalRule: false,
                      italic: false,
                      listItem: false,
                      orderedList: false,
                      strike: false,
                      ...props.starterKit,
                  }
                : {
                      code: false,
                      ...props.starterKit,
                  },
        ),
        useMention<FieldDefinition>({
            class: "hd-field-variable",
            attributes: ["label", "key"],
            matchKey: "key",
            prefix: "{{",
            suffix: "}}",
            items: computed(() => props.fields),
            render: (item) => {
                return item ? `{{${item.key}}}` : `{{undefined}}`;
            },
            query: ({ items, query }) => {
                if (query) {
                    const fuse = new Fuse(items, {
                        shouldSort: true,
                        keys: [
                            "label",
                            "key",
                            "options.label",
                            "options.value",
                            "default_value",
                        ],
                        isCaseSensitive: false,
                        threshold: 0.5,
                        useExtendedSearch: true,
                        ignoreLocation: true,
                    });

                    return fuse.search(query).map(({ item }) => {
                        return item;
                    });
                }

                return items;
            },
        }),
        ...props.extensions,
    ],
    editorProps: {
        attributes: {
            class: inputInnerClass.value,
        },
    },
    content: content.value,
    onUpdate: ({ editor }) => {
        if (props.mode === "html") {
            content.value = editor.getHTML();
        } else if (props.mode === "text") {
            const json = editor.getJSON();

            if (json.content.length > 1 && !props.textarea) {
                processText(props.modelValue);
            } else if (
                json.content.length === 1 &&
                json.content[0].type !== "paragraph"
            ) {
                json.content[0].type = "paragraph";
                editor.commands.setContent(json);
            } else {
                content.value = editor.getText();
            }
        } else if (props.mode === "json") {
            content.value = editor.getJSON();
        }

        emitFormInput();
    },
    onFocus() {
        focused.value = true;
    },
    onBlur() {
        focused.value = false;
    },
});

const { blocks, activeBlock, styles, aligns, activeAlign } = useEditorBase(
    editor as Ref<Editor>,
);

const canBeAutoFocused = ref<boolean>(!!props.autofocus);

function processText(text: string, force: boolean = false) {
    let mode = props.mode;

    if (
        !content.value ||
        (mode !== "text" && !force) ||
        typeof text !== "string"
    ) {
        if (canBeAutoFocused.value) {
            canBeAutoFocused.value = false;
            editor.value?.commands.focus();
        }
        return;
    }

    if (mode === "text" && text?.trim()?.includes("\n\n")) {
        mode = "html";
        text = text
            .split("\n\n")
            .map((txt) => `<p>${txt}</p>`)
            .join("");
    }

    let match: RegExpExecArray | null;
    const jsonContent = [];

    const mentionMatches = [
        { between: ["{{", "}}"], name: "mention" },
        ...props.mentionMatch,
    ];

    const regexParts = mentionMatches.map((pattern) => {
        const [prefix, suffix] = pattern.between;
        return `${prefix}\\s*(.*?)\\s*${suffix}`;
    });

    function test(testText: string) {
        return new RegExp(regexParts.join("|"), "g").exec(testText as string);
    }

    while (true) {
        match = test(text as string);

        if (match === null) break;

        const last = Array.from(match)
            .reverse()
            .filter((m) => !!m)[0];

        const mentionMatch = mentionMatches.find((mentionMatch) => {
            return (
                match[0].startsWith(mentionMatch.between[0]) &&
                match[0].endsWith(mentionMatch.between[1])
            );
        });

        let field = undefined;

        if (mentionMatch?.fields) {
            field = (
                isRef(mentionMatch?.fields)
                    ? toValue(mentionMatch?.fields)
                    : (mentionMatch?.fields ?? [])
            ).find((field: FieldDefinition) => field.key === last);
        } else {
            field = props.fields.find(
                (field: FieldDefinition) => field.key === last,
            );
        }

        if (mode === "text") {
            const split = text.split(match[0]);

            if (split[0]) {
                jsonContent.push({ type: "text", text: split[0] });
            }

            if (field) {
                jsonContent.push(
                    field
                        ? {
                              type: mentionMatch.name ?? "mention",
                              attrs: {
                                  label: field.label,
                                  key: field.key,
                              },
                          }
                        : { type: "text", text: match[0] },
                );
            } else {
                jsonContent.push({
                    type: "text",
                    text: match[0],
                });
            }

            split.shift();

            text = split.join(match[0]) ?? "";
        } else {
            const span = document.createElement("span");
            span.classList.add(`hd-${mentionMatch?.name ?? "mention"}`);
            span.setAttribute("data-type", mentionMatch?.name ?? "mention");
            span.setAttribute("data-key", field?.key ?? match[1]);
            span.setAttribute("data-label", field?.label ?? match[1]);
            span.innerText = field?.label ?? match[1];
            text = text.replaceAll(match[0], span.outerHTML);
        }
    }

    if (mode === "html") {
        editor.value?.chain().clearContent().setContent(text).run();
    } else {
        if (text) {
            jsonContent.push({ type: "text", text: text });
        }

        editor.value?.chain().clearContent().run();
        editor.value
            ?.chain()
            .setContent({
                type: "doc",
                content: [
                    {
                        type: "paragraph",
                        attrs: {
                            textAlign: "left",
                        },
                        content: jsonContent,
                    },
                ],
            })
            .run();
    }

    if (canBeAutoFocused.value) {
        canBeAutoFocused.value = false;
        editor.value?.commands.focus();
    }
}

onMounted(() => {
    processText(content.value as string, true);
});

watch(
    () => props.modelValue,
    (to) => {
        if (content.value !== to) {
            processText(to, true);
        }
    },
);

watch(content, (to) => {
    if (props.mode === "json" || props.mode === "html") {
        emit("update:modelValue", to);
    } else {
        const tag = document.createElement(props.outerTag ?? "p");
        tag.innerHTML = to;

        if (props.mode === "text") {
            to = tag.innerText?.trim();
        } else if (props.stripOuterTag) {
            to = tag.children.length ? tag.children[0].innerHTML : "";
        } else {
            const child = tag.children[0];

            if (child) {
                for (const attribute of child.getAttributeNames())
                    tag.setAttribute(attribute, child.getAttribute(attribute));
                tag.innerHTML = child.innerHTML;
            }

            if (props.outerClass) {
                tag.classList.add(
                    ...(Array.isArray(props.outerClass)
                        ? props.outerClass
                        : [props.outerClass]),
                );
            }

            if (props.outerStyle) {
                for (const [key, value] of Object.entries(props.outerStyle)) {
                    tag.style[key] = value;
                }
            }

            to = tag.outerHTML;
        }

        emit("update:modelValue", to?.trim());
    }
});

const proseClass = computed(() => {
    if (props.unstyled) {
        return "";
    }

    let maxWidth = props.maxWidth;

    if (props.variant === "none") {
        maxWidth = "max-w-full";
    }

    return (
        `prose ${maxWidth} ` +
        ({
            "2xs": `prose-sm ${props.snug ? " leading-3" : ""}`,
            xs: `prose-sm ${props.snug ? " leading-none" : ""}`,
            sm: `prose-sm ${props.snug ? " leading-tight" : ""}`,
            md: `prose-base ${props.snug ? " leading-snug" : ""}`,
            lg: `prose-lg ${props.snug ? " leading-normal" : ""}`,
            xl: `prose-xl ${props.snug ? " leading-loose" : ""}`,
        }[props.size] ?? "")
    );
});

const mentions = computed(() => {
    return (props.fields ?? []).map((field) => ({
        label: field.label,
        key: field.key,
        avatar: field.avatar,
        type: field.type,
        click: () => {
            editor.value
                ?.chain()
                .insertContent({
                    type: "mention",
                    attrs: {
                        key: field.key,
                        label: field.label,
                    },
                })
                .focus()
                .run();
        },
    }));
});

const mentionSearch = ref("");
const options = ref<
    UseFuseOptions<{
        label: string;
        key: string;
        avatar: Record<string, any>;
        click: () => void;
    }>
>({
    fuseOptions: {
        shouldSort: true,
        keys: [
            "label",
            "key",
            "options.label",
            "options.value",
            "default_value",
        ],
        isCaseSensitive: false,
        threshold: 0.5,
        useExtendedSearch: true,
        ignoreLocation: true,
    },
    resultLimit: 20,
    matchAllWhenSearchEmpty: true,
});

const { results } = useFuse(mentionSearch, mentions, options);

defineExpose({
    editor,
});
</script>

<template>
    <div
        v-if="editor"
        :class="[
            ui.wrapper,
            disabled ? 'disabled cursor-not-allowed' : undefined,
        ]"
    >
        <div
            :class="[
                unstyled ? undefined : inputOuterClass,
                {
                    'pointer-events-none cursor-not-allowed select-none':
                        disabled,
                },
            ]"
            tabindex="0"
        >
            <HdInputMenu
                v-if="withMenu"
                :class="padded ? ui.padding[sizeGroup] : 'p-0'"
            >
                <div class="flex items-center">
                    <UDropdown
                        v-if="blockType === 'list'"
                        :items="[blocks]"
                        :popper="{ placement: 'bottom-start' }"
                        :size="sizeGroup"
                    >
                        <UButton
                            v-if="activeBlock"
                            :color="color"
                            :size="sizeGroup"
                            :square="menuMode === 'icons'"
                            :ui="{ rounded: 'rounded-l-md rounded-r-none' }"
                        >
                            <template #leading>
                                <span :class="{ 'px-1': menuMode === 'icons' }">
                                    <FontAwesomeIcon :icon="activeBlock.icon" />
                                </span>
                            </template>
                            <span v-if="menuMode !== 'icons'">{{
                                activeBlock.label
                            }}</span>
                        </UButton>
                        <UButton
                            v-else
                            :color="color"
                            :size="sizeGroup"
                            :ui="{ rounded: 'rounded-l-md rounded-r-none' }"
                        >
                            <template #leading>
                                <span :class="{ 'px-1': menuMode === 'icons' }">
                                    <FontAwesomeIcon :icon="faLayerGroup" />
                                </span>
                            </template>
                            <span v-if="menuMode !== 'icons'">Block</span>
                        </UButton>
                        <template #item="{ item }">
                            <FontAwesomeIcon :icon="item.icon" />
                            <span class="truncate">{{ item.label }}</span>
                        </template>
                    </UDropdown>
                    <UDropdown
                        v-if="styleType === 'list'"
                        :items="[styles]"
                        :popper="{ placement: 'bottom-start' }"
                        :size="sizeGroup"
                    >
                        <UButton
                            :color="color"
                            :size="sizeGroup"
                            :ui="{ rounded: 'rounded-none' }"
                        >
                            <template #leading>
                                <span>
                                    <FontAwesomeIcon :icon="faPenSwirl" />
                                </span>
                            </template>
                            <span v-if="menuMode !== 'icons'">Style</span>
                        </UButton>
                        <template #item="{ item }">
                            <FontAwesomeIcon
                                :class="{
                                    'text-hd-secondary-original': item.active,
                                }"
                                :icon="item.icon"
                            />
                            <span
                                :class="{
                                    'text-hd-secondary-original': item.active,
                                }"
                                class="truncate"
                                >{{ item.label }}</span
                            >
                        </template>
                    </UDropdown>
                    <UDropdown
                        v-if="styleType === 'list'"
                        :items="[aligns]"
                        :popper="{ placement: 'bottom-start' }"
                    >
                        <UButton
                            :color="color"
                            :size="sizeGroup"
                            :ui="{ rounded: 'rounded-none' }"
                        >
                            <span>
                                <FontAwesomeIcon
                                    :icon="
                                        activeAlign
                                            ? activeAlign.icon
                                            : faAlignSlash
                                    "
                                />
                            </span>
                        </UButton>
                        <template #item="{ item }">
                            <FontAwesomeIcon :icon="item.icon" />
                            <span class="truncate">{{ item.label }}</span>
                        </template>
                    </UDropdown>
                    <UPopover
                        :popper="{ placement: 'bottom-end' }"
                        :ui="{
                            container:
                                'overflow-hidden shadow-lg border border-gray-300 rounded-md',
                        }"
                    >
                        <UButton
                            :color="color"
                            :size="sizeGroup"
                            :ui="{ rounded: 'rounded-l-none rounded-r-md' }"
                            square
                        >
                            <span>
                                <FontAwesomeIcon
                                    :icon="faBracketsCurly"
                                    fixed-width
                                />
                            </span>
                        </UButton>
                        <template #panel>
                            <AppFieldList
                                :fields="fields"
                                :input="{
                                    placeholder: 'Search fields...',
                                    class: 'w-full border-b -mb-0',
                                    variant: 'none',
                                }"
                                as-div
                                class="[&>div:last-child]:max-h-[15rem] [&>div:last-child_.ui-data-list-item]:!pl-1"
                                show
                                @keydown.space.stop
                            />
                        </template>
                    </UPopover>
                </div>
                <UButtonGroup :size="sizeGroup">
                    <slot :editor="editor" name="left-menu-start" />
                    <template v-if="blockType === 'buttons'">
                        <UButton
                            v-for="(btn, idx) in blocks"
                            :key="idx"
                            :color="btn.disabled ? 'primary' : color"
                            :disabled="btn.disabled"
                            square
                            @click="btn.click"
                        >
                            <span
                                ><FontAwesomeIcon :icon="btn.icon" fixed-width
                            /></span>
                        </UButton>
                    </template>
                    <slot :editor="editor" name="left-menu-end" />
                </UButtonGroup>
                <UButtonGroup size="md">
                    <slot :editor="editor" name="right-menu-start" />
                    <UButton
                        :color="color"
                        :ui="{
                            rounded: 'rounded-r-none',
                        }"
                        square
                        @click="() => editor.chain().undo().run()"
                    >
                        <span
                            ><FontAwesomeIcon :icon="faUndo" fixed-width
                        /></span>
                    </UButton>
                    <UButton
                        :color="color"
                        :ui="{
                            rounded: 'rounded-l-none',
                        }"
                        square
                        @click="() => editor.chain().redo().run()"
                    >
                        <span
                            ><FontAwesomeIcon :icon="faRedo" fixed-width
                        /></span>
                    </UButton>
                    <slot :editor="editor" name="right-menu-end" />
                </UButtonGroup>
            </HdInputMenu>
            <slot :editor="editor" name="pre-content" />
            <TiptapEditorContent
                :class="unstyled ? undefined : [proseClass, proseModifiers]"
                :editor="editor"
                :style="unstyled ? undefined : proseStyle"
            >
            </TiptapEditorContent>
            <slot :editor="editor" name="post-content" />
        </div>

        <slot />

        <span
            v-if="(isLeading && leadingIconName) || $slots.leading"
            :class="leadingWrapperIconClass"
        >
            <slot :disabled="disabled" :loading="loading" name="leading">
                <UIcon :class="leadingIconClass" :name="leadingIconName" />
            </slot>
        </span>

        <span
            v-if="(isTrailing && trailingIconName) || $slots.trailing"
            :class="trailingWrapperIconClass"
        >
            <slot :disabled="disabled" :loading="loading" name="trailing">
                <UIcon :class="trailingIconClass" :name="trailingIconName" />
            </slot>
        </span>

        <template v-if="editor && $slots['full-bubble-menu']">
            <slot :editor="editor" name="full-bubble-menu" />
        </template>
        <TiptapBubbleMenu
            v-else-if="editor && withBubbleMenu"
            :class="bubbleMenuClass"
            :editor="editor"
        >
            <slot :editor="editor" name="bubble-menu" />
        </TiptapBubbleMenu>
    </div>
</template>

<style>
.hd-field-variable {
    @apply border rounded-md border-gray-300 px-1.5 py-0.5;
}

.disabled img.ProseMirror-separator {
    display: none !important;
}

p[data-placeholder] {
    position: relative;
}

p[data-placeholder]::after {
    position: absolute;
    left: 0;
    top: 0;
    color: darkgray;
    content: attr(data-placeholder);
    font-weight: 400;
    display: block;
    white-space: nowrap !important;
    pointer-events: none;
}
</style>
